// Revisión anual del salario básico (Art. 81 CT). Nómina → Parámetros.
// Tres tiempos a propósito: preparar (no toca nada), ajustar línea por línea
// —el mínimo legal es el piso, no el techo— y aplicar dejando acta sellada.
// Subir sueldos es irreversible: bajarlos otra vez sería reducción de
// remuneración, así que no se hace de un clic.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCheck2, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Modal, fieldCls, money, nomRpc, useDialogoEnLinea, type SbuRevision, type SbuRevisionLinea } from './shared';

interface Detalle { cabecera: SbuRevision | null; lineas: SbuRevisionLinea[] }

export function RevisionSbuModal({ anio, onClose }: { anio: number; onClose: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [id, setId] = useState('');
  const [motivo, setMotivo] = useState(`Revisión anual del salario básico ${anio}`);

  const { data: revisiones = [] } = useQuery<SbuRevision[]>({
    queryKey: ['nom-sbu-revisiones'],
    queryFn: () => nomRpc<SbuRevision[]>('nom_sbu_revisiones'),
  });
  const actual = revisiones.find((r) => r.id === id)
    ?? revisiones.find((r) => r.anio === anio && r.estado === 'borrador')
    ?? revisiones.find((r) => r.anio === anio)
    ?? null;

  const { data: det, isFetching } = useQuery<Detalle>({
    queryKey: ['nom-sbu-revision', actual?.id],
    queryFn: () => nomRpc<Detalle>('nom_sbu_revision', { _id: actual!.id }),
    enabled: !!actual,
  });

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['nom-sbu-revisiones'] });
    void qc.invalidateQueries({ queryKey: ['nom-sbu-revision'] });
    void qc.invalidateQueries({ queryKey: ['nom-contratos-bajo-sbu'] });
    void qc.invalidateQueries({ queryKey: ['nom-contratos'] });
  };

  const preparar = useMutation({
    mutationFn: () => nomRpc<{ id: string; contratos: number; costo_anual: number }>(
      'nom_sbu_revision_preparar', { _anio: anio }),
    onSuccess: (r) => {
      setId(r.id); refrescar();
      toast.success(r.contratos === 0
        ? 'Ningún contrato vigente está por debajo del básico'
        : `${r.contratos} contrato(s) por debajo del básico · costo anual ${money(r.costo_anual)}`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const editar = useMutation({
    mutationFn: (v: { linea: string; sueldo?: number; aplicar?: boolean }) =>
      nomRpc('nom_sbu_revision_linea', {
        _linea: v.linea, _sueldo_nuevo: v.sueldo ?? null, _aplicar: v.aplicar ?? null,
      }),
    onSuccess: refrescar,
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const aplicar = useMutation({
    mutationFn: (confirmar: boolean) => nomRpc<{ aplicada: boolean; mensaje?: string; contratos?: number; hash_acta?: string }>(
      'nom_sbu_revision_aplicar', { _id: actual!.id, _confirmar: confirmar, _motivo: motivo }),
    onSuccess: async (r) => {
      if (r.aplicada) {
        toast.success(`${r.contratos} contrato(s) subidos · acta ${String(r.hash_acta).slice(0, 12)}…`);
        refrescar();
      } else {
        if (await dlg.confirmar('¿Aplicar la subida?', {
          detalle: `${r.mensaje}\n\nEsto no se puede deshacer: bajar un sueldo después sería reducción de remuneración (Art. 81 CT).`,
          aceptar: 'Aplicar',
        })) {
          aplicar.mutate(true);
        }
      }
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const lineas = det?.lineas ?? [];
  const cab = det?.cabecera ?? null;
  const aplicada = cab?.estado === 'aplicada';

  return (
    <Modal title={`Revisión del salario básico ${anio}`} onClose={onClose} wide>
      <div className="space-y-4">
        {dlg.ui}
        <div className="flex flex-wrap items-center gap-3">
          <Button isLoading={preparar.isPending} onClick={() => preparar.mutate()} variant={cab ? 'outline' : 'default'}>
            <ShieldAlert className="w-4 h-4 mr-1" /> {cab ? 'Volver a revisar' : 'Revisar contratos'}
          </Button>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          {revisiones.length > 1 && (
            <select className={`${fieldCls} w-56`} value={actual?.id ?? ''} onChange={(e) => setId(e.target.value)}
              aria-label="Revisión">
              {revisiones.map((r) => (
                <option key={r.id} value={r.id}>{r.anio} · {r.estado} · {r.n_contratos} contrato(s)</option>
              ))}
            </select>
          )}
        </div>

        {cab && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><div className="text-xs text-muted-foreground">Básico de referencia</div><div className="text-lg font-semibold tabular-nums">{money(cab.sbu)}</div></div>
            <div><div className="text-xs text-muted-foreground">Contratos a subir</div><div className="text-lg font-semibold tabular-nums">{cab.n_contratos}</div></div>
            <div><div className="text-xs text-muted-foreground">Costo anual</div><div className="text-lg font-semibold tabular-nums">{money(cab.costo_anual)}</div></div>
            <div><div className="text-xs text-muted-foreground">Estado</div><div className="text-lg font-semibold">{cab.estado}</div></div>
          </div>
        )}

        {cab && lineas.length > 0 && (
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card"><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-2">Sube</th><th className="py-2 pr-2">Empleado</th>
                <th className="py-2 pr-2">Cargo</th><th className="py-2 pr-2 text-right">Jornada</th>
                <th className="py-2 pr-2 text-right">Gana</th><th className="py-2 pr-2 text-right">Mínimo</th>
                <th className="py-2 pr-2 text-right">Pasa a</th><th className="py-2 pr-2 text-right">Aumento</th>
              </tr></thead>
              <tbody>
                {lineas.map((l) => (
                  <tr key={l.id} className="border-b last:border-0">
                    <td className="py-1 pr-2">
                      <input type="checkbox" checked={l.aplicar} disabled={aplicada}
                        onChange={(e) => editar.mutate({ linea: l.id, aplicar: e.target.checked })} />
                    </td>
                    <td className="py-1 pr-2">{l.empleado}<div className="text-xs text-muted-foreground font-mono">{l.empleado_codigo}</div></td>
                    <td className="py-1 pr-2 text-muted-foreground">{l.cargo ?? '—'}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{Number(l.horas_semanales)} h</td>
                    <td className="py-1 pr-2 text-right font-mono tabular-nums">{money(l.sueldo_anterior)}</td>
                    <td className="py-1 pr-2 text-right font-mono tabular-nums text-muted-foreground">{money(l.minimo)}</td>
                    <td className="py-1 pr-2 text-right">
                      <input type="number" step="0.01" disabled={aplicada}
                        className={`${fieldCls} h-8 w-28 text-right`} defaultValue={Number(l.sueldo_nuevo)}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (v && v !== Number(l.sueldo_nuevo)) editar.mutate({ linea: l.id, sueldo: v });
                        }} />
                    </td>
                    <td className="py-1 pr-2 text-right font-mono tabular-nums text-ok">{money(l.aumento)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {cab && lineas.length === 0 && (
          <div className="py-8 text-center text-muted-foreground text-sm">
            Ningún contrato vigente está por debajo del básico de {anio}. No hay nada que subir.
          </div>
        )}

        {cab && !aplicada && lineas.length > 0 && (
          <div className="space-y-2">
            <input className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="Motivo que quedará en el acta" />
            <p className="text-xs text-muted-foreground">
              El mínimo legal es el piso, no el techo: puedes subir a alguien por encima escribiendo otro valor.
              Al aplicar queda un <b>acta</b> con quién pasó de cuánto a cuánto, sellada con su huella.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
              <Button isLoading={aplicar.isPending} onClick={() => aplicar.mutate(false)}>
                Aplicar la subida
              </Button>
            </div>
          </div>
        )}

        {aplicada && cab && (
          <div className="rounded-md border border-ok/40 bg-ok/5 p-3 text-sm flex items-start gap-2">
            <FileCheck2 className="w-4 h-4 mt-0.5 text-ok shrink-0" />
            <div>
              Aplicada{cab.aplicada_at ? ` el ${cab.aplicada_at.slice(0, 10)}` : ''}
              {cab.aplicada_por ? ` por ${cab.aplicada_por}` : ''}: {cab.n_contratos} contrato(s),
              costo anual {money(cab.costo_anual)}.
              <div className="text-xs text-muted-foreground font-mono mt-1">acta {cab.hash_acta}</div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
